import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const StripeConnectCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Checking your Stripe account...');
  const [accountInfo, setAccountInfo] = useState(null);

  useEffect(() => {
    const checkAccountStatus = async () => {
      try {
        // scope/organization_id are forwarded from the Connect authorize redirect so we
        // check status for the account that was actually being connected (org vs. coach).
        const incomingParams = new URLSearchParams(window.location.search);
        const statusParams = new URLSearchParams();
        statusParams.set('scope', incomingParams.get('scope') || 'coach');
        if (incomingParams.get('organization_id')) {
          statusParams.set('organization_id', incomingParams.get('organization_id'));
        }

        const response = await fetch(`/api/stripe-accounts/connect/status?${statusParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAccountInfo(data);

          if (data.connected && data.ready) {
            setStatus('success');
            setMessage('Your Stripe account is connected and ready to accept payments!');
          } else if (data.connected && !data.ready) {
            setStatus('warning');
            setMessage('Your account is connected but setup is incomplete. Please complete the required information.');
          } else {
            setStatus('error');
            setMessage('Unable to verify your Stripe account connection.');
          }

          // Redirect to profile after 3 seconds
          setTimeout(() => {
            navigate('/profile?stripe_refresh=true');
          }, 3000);
        } else {
          setStatus('error');
          setMessage('Failed to verify account status. Please try again.');
          setTimeout(() => {
            navigate('/profile');
          }, 3000);
        }
      } catch (error) {
        console.error('Error checking account status:', error);
        setStatus('error');
        setMessage('An error occurred while checking your account.');
        setTimeout(() => {
          navigate('/profile');
        }, 3000);
      }
    };

    checkAccountStatus();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          {status === 'processing' && (
            <div className="mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          )}

          {status === 'success' && (
            <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
          )}

          {status === 'warning' && (
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
          )}

          {status === 'error' && (
            <XCircleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
          )}

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {status === 'processing' && 'Processing...'}
            {status === 'success' && 'Success!'}
            {status === 'warning' && 'Setup Incomplete'}
            {status === 'error' && 'Error'}
          </h2>

          <p className="text-gray-600 mb-6">{message}</p>

          {accountInfo?.account && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="text-sm space-y-2">
                {accountInfo.account.business_name && (
                  <p><span className="font-medium">Business:</span> {accountInfo.account.business_name}</p>
                )}
                {accountInfo.account.email && (
                  <p><span className="font-medium">Email:</span> {accountInfo.account.email}</p>
                )}
                <div className="flex items-center space-x-2 mt-2">
                  <span className={`text-xs px-2 py-1 rounded ${accountInfo.account.charges_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {accountInfo.account.charges_enabled ? 'Charges Enabled' : 'Charges Pending'}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${accountInfo.account.payouts_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {accountInfo.account.payouts_enabled ? 'Payouts Enabled' : 'Payouts Pending'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-500">
            Redirecting to your profile...
          </p>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectCallback;

